import { useSessionContext } from '@/contexts/session/context';
import { groupsApis, groupDB } from '@/configs/apis/groups';
import { chatApis } from '@/configs/apis/chat';
import { safeJson, withRetry } from '@/scripts/utils';
import type { GroupEncryptInfoParams } from '@/configs/apis/groups/interface';
import { useRefs } from '@/scripts/hooks';
import Queue from '@/scripts/utils/queue';
import { pushLog } from '@/scripts/utils/devUtils';
import { UserKeyPackageType } from '@/configs/apis/type';
import type { ChatMessageMessageIdParams } from '../useChatMessages/types';

const defaultParams: any = {
  version: import.meta.env.VITE_ENCRYPT_SINGLE_VERSION,
  type: UserKeyPackageType.GROUP,
};
export function useGroupEncryption() {
  const {
    session: { user },
  } = useSessionContext();
  const [hasGroupKey, setHasGroupKey] = useState(true);

  const queues = useRef<Record<string, Queue>>({});

  const handleSyncApplyCommit = useCallback(
    async ({ groupId, epoch }: { groupId: string; epoch: number }) => {
      try {
        const { data: localEpoch } = await groupDB.epoch(groupId);
        if (epoch > localEpoch) {
          const diffNum = epoch - localEpoch;
          const diffEpochs = Array.from(
            { length: diffNum },
            (_, i) => localEpoch + 1 + i
          );

          const commits = await groupsApis.getGroupEncryptCommits({
            gid: groupId,
            epochs: diffEpochs,
          });

          for (const { gid, commit } of commits) {
            await groupDB.applyCommit({ groupId: gid, commitMsg: commit });
          }
        }
      } catch (error: any) {
        pushLog('group-getGroupEncryptCommits', error?.message);
      }
    },
    []
  );

  const checkAndSetKeyPackage = useCallback(async (userId: string | undefined) => {
    try {
      if (!userId) {
        throw new Error('No found user id');
      }
      const baseParams = { ...defaultParams, userId };
      const userPackage = await chatApis.getUserPackage(baseParams);
      if (!userPackage) {
        const { data: keyPackage } = await groupDB.getKeyPacakge();
        await groupsApis.updateMemberkeyPackage({
          uid: userId,
          key: keyPackage,
          hash_ref: '',
        });
        chatApis.setUserPackage({ ...baseParams, keyPackage });
      }
      setHasGroupKey(true);
    } catch (error: any) {
      pushLog('group-keyPackage-check', error?.message);
      setHasGroupKey(false);
      throw error;
    }
  }, []);

  const encryptMessage = useCallback(async (groupId: string, text: string) => {
    try {
      const { data: localEpoch } = await groupDB.epoch(groupId);
      const { data: ciphertext } = await groupDB.encryptMessage({ groupId, text });
      return { ciphertext, epoch: localEpoch };
    } catch (error: any) {
      pushLog('group-encryptMessage', error?.message);
      return { ciphertext: text, epoch: -1 };
    }
  }, []);

  const decryptMessage = useCallback(
    async (groupId: string, message: ChatMessageMessageIdParams) => {
      try {
        const { data: localEpoch } = await groupDB.epoch(groupId);
        if (localEpoch < (message?.attach || 0)) {
          await handleSyncApplyCommit({ groupId, epoch: message.attach! });
        }
        const { data } = await groupDB.decryptMessage({
          groupId,
          text: message.payload as string,
        });
        return data;
      } catch (error: any) {
        pushLog('group-decryptMessage', error?.message);
        return 'Decryption failed';
      }
    },
    [handleSyncApplyCommit]
  );

  const getEpochAndGroupInfo = useCallback(async (groupId: string) => {
    const [epochRes, groupInfoRes] = await Promise.all([
      groupDB.epoch(groupId),
      groupDB.getGroupInfo(groupId),
    ]);
    return { epoch: epochRes.data + 1, groupInfo: groupInfoRes.data };
  }, []);

  const leaveGroup = useCallback((groupId: string) => {
    return groupDB.leaveGroup(groupId);
  }, []);

  const deleteGroup = useCallback((groupId: string) => {
    return groupDB.deleteGroup(groupId);
  }, []);

  const initLoadGroups = useCallback(async (groupIds: string[]) => {
    await groupDB.loadGroups(groupIds).catch((_) => '');
  }, []);

  const handleJoinGroup = useCallback(
    async (pulledMessage: ChatMessageMessageIdParams) => {
      try {
        const { gid, welcome } = safeJson<Record<string, any>>(
          pulledMessage.payload!,
          {}
        );
        await groupDB.encryptJoinGroup({ groupId: gid, welcomeMsg: welcome });
      } catch (error: any) {
        pushLog('group-JoinGroup', error?.message);
      }
    },
    []
  );

  const handleApplyCommit = useCallback(
    async (pulledMessage: ChatMessageMessageIdParams) => {
      try {
        const { gid, epoch, commit, user_id } = safeJson<Record<string, any>>(
          pulledMessage.payload!,
          {}
        );
        const { data: localEpoch } = await groupDB.epoch(gid);
        if (epoch === localEpoch + 1 && commit) {
          const { data } = await groupDB.contains(gid);
          if (data !== '1' && epoch === 1 && user_id === user?.userId) {
            await groupDB.createGroupWithId(gid);
          }
          await groupDB.applyCommit({ groupId: gid, commitMsg: commit });
        } else {
          pushLog('group-applyCommitParamsError', { epoch, localEpoch, gid });
        }
      } catch (error: any) {
        pushLog('group-applyCommit', error?.message?.message);
      }
    },
    [user?.userId]
  );

  const handleApplyCommitQueue = useCallback(
    async (chat_id: string, pulledMessage: ChatMessageMessageIdParams) => {
      if (!queues.current?.[chat_id]) {
        queues.current[chat_id] = new Queue();
      }
      queues.current[chat_id]?.push(() => handleApplyCommit(pulledMessage));
    },
    [handleApplyCommit]
  );

  const joinGroupByExternal = useCallback(
    async (encryptInfo: GroupEncryptInfoParams, retryCount = 3) => {
      try {
        const { data } = await groupDB.contains(encryptInfo.gid);
        if (data === '1') {
          return;
        }
        if (encryptInfo?.data) {
          const { data: commit } = await groupDB.joinByExternalCommit({
            groupId: encryptInfo.gid,
            groupInfo: encryptInfo.data,
          });
          const { epoch, groupInfo } = await getEpochAndGroupInfo(encryptInfo.gid);
          return await groupsApis.refreshGroupEncryptInfo({
            gid: encryptInfo.gid,
            epoch: epoch - 1,
            data: groupInfo,
            commit,
          });
        } else {
          throw new Error('No found group info');
        }
      } catch (error: any) {
        if (error.code === '10010' && retryCount > 0) {
          const newEncryptInfo = await groupsApis
            .getGroupEncryptInfo(encryptInfo.gid)
            .catch((_) => null);
          await deleteGroup(encryptInfo.gid);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return joinGroupByExternal(newEncryptInfo || encryptInfo, retryCount - 1);
        } else {
          pushLog('group-joinGroupExternal', error?.message);
        }
      }
    },
    [getEpochAndGroupInfo, deleteGroup]
  );

  const joinGroupByExternalGroups = useCallback(async () => {
    const { encryption } = await groupsApis.getGroupIds();
    if (encryption.length) {
      const nums = Math.ceil(encryption.length / 20);
      for (let i = 0; i < nums; i++) {
        const groupIds = encryption.slice(i * 20, (i + 1) * 20);
        const groupEncryptInfos = await groupsApis
          .getGroupEncryptInfos(groupIds)
          .catch((_) => []);
        if (groupEncryptInfos.length) {
          await Promise.allSettled(
            groupEncryptInfos.map(async (groupEncryptInfo) => {
              return joinGroupByExternal(groupEncryptInfo);
            })
          );
        }
      }
    }
  }, [joinGroupByExternal]);

  const leaveMembersWithUpdateInfo = useCallback(
    async ({ groupId, uids }: { groupId: string; uids: string[] }, retryCount = 3) => {
      try {
        await checkAndSetKeyPackage(user?.userId);
        const { commit, group_info: groupInfo } = await groupDB.encryptRemoveMembers({
          groupId,
          userIds: uids,
        });
        const { epoch } = await getEpochAndGroupInfo(groupId);
        return await groupsApis.refreshGroupEncryptInfo({
          gid: groupId,
          epoch,
          data: groupInfo,
          commit,
        });
      } catch (error: any) {
        if (error.code === '10010' && retryCount > 0) {
          await handleSyncApplyCommit({
            groupId,
            epoch: error.response?.data?.extra?.epoch ?? 0,
          });
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return leaveMembersWithUpdateInfo({ groupId, uids });
        } else {
          pushLog('group-leaveGroup', error?.message);
        }
      }
    },
    [checkAndSetKeyPackage, getEpochAndGroupInfo, handleSyncApplyCommit, user?.userId]
  );

  const removeMembersWithUpdateInfo = useCallback(
    async ({ groupId, uids }: { groupId: string; uids: string[] }, retryCount = 3) => {
      try {
        await checkAndSetKeyPackage(user?.userId);
        const { commit, group_info: groupInfo } = await groupDB.encryptRemoveMembers({
          groupId,
          userIds: uids,
        });
        const { epoch } = await getEpochAndGroupInfo(groupId);
        return await groupsApis.removeGroupEncryptMembers({
          gid: groupId,
          uids,
          epoch,
          data: groupInfo,
          commit,
        });
      } catch (error: any) {
        if (error.code === '10010' && retryCount > 0) {
          await handleSyncApplyCommit({
            groupId,
            epoch: error.response?.data?.extra?.epoch ?? 0,
          });
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return removeMembersWithUpdateInfo({ groupId, uids }, retryCount - 1);
        } else {
          pushLog('group-removeMembers', error?.message);
          throw new Error('Remove members failed');
        }
      }
    },
    [checkAndSetKeyPackage, getEpochAndGroupInfo, handleSyncApplyCommit, user?.userId]
  );

  const addMembersWithUpdateInfo = useCallback(
    async (
      { groupId, uids }: { groupId: string; uids: string[] },
      retryCount = 3
    ): Promise<any> => {
      try {
        await checkAndSetKeyPackage(user?.userId);
        const membersPackages = await groupsApis.getMemberskeyPackages(uids);
        if (membersPackages?.length) {
          const {
            welcome,
            commit,
            group_info: groupInfo,
          } = await groupDB.encryptAddMembers({
            groupId,
            targetPackages: membersPackages.map((memberPackage) => memberPackage.key),
          });
          const { epoch } = await getEpochAndGroupInfo(groupId);

          return await groupsApis.addGroupEncryptMembers({
            gid: groupId,
            uids,
            epoch,
            data: groupInfo,
            welcome,
            commit,
          });
        } else {
          throw new Error('No found members packages');
        }
      } catch (error: any) {
        if (error.code === '10010' && retryCount > 0) {
          await handleSyncApplyCommit({
            groupId,
            epoch: error.response?.data?.extra?.epoch ?? 0,
          });
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return addMembersWithUpdateInfo({ groupId, uids }, retryCount - 1);
        } else {
          pushLog('group-addMembers', error?.message);
          throw new Error('Add members failed');
        }
      }
    },
    [checkAndSetKeyPackage, getEpochAndGroupInfo, handleSyncApplyCommit, user?.userId]
  );

  const createGroupEncryption = useCallback(
    async (groupId: string) => {
      try {
        await withRetry(() => checkAndSetKeyPackage(user?.userId), {
          beforeRetry: () => new Promise((resolve) => setTimeout(resolve, 1000)),
        })();
        await groupDB.createGroupWithId(groupId);
        const { epoch, groupInfo } = await getEpochAndGroupInfo(groupId);
        await groupsApis.updateGroupEncryptInfo({
          gid: groupId,
          epoch: epoch - 1,
          data: groupInfo,
        });
      } catch (error: any) {
        pushLog('group-createGroup', error?.message);
      }
    },
    [user?.userId, checkAndSetKeyPackage, getEpochAndGroupInfo]
  );

  const initCreateGroupEncryption = useCallback(
    async (pulledMessage: ChatMessageMessageIdParams) => {
      try {
        const { group_id, user_id } = safeJson<Record<string, any>>(
          pulledMessage.payload!,
          {}
        );
        const { data } = await groupDB.contains(group_id);
        if (data !== '1' && user_id === user?.userId) {
          await groupDB.createGroupWithId(group_id);
        }
      } catch (error: any) {
        pushLog('group-initCreateGroup', error?.message);
      }
    },
    [user?.userId]
  );

  const initCrypto = useCallback(
    async (userId: string) => {
      try {
        const isInitCrypto = await chatApis.getInitCrypto();
        if (!isInitCrypto) {
          joinGroupByExternalGroups();
          chatApis.setInitCrypto(true);
        }
        withRetry(() => checkAndSetKeyPackage(userId), {
          beforeRetry: () => new Promise((resolve) => setTimeout(resolve, 1000)),
        })();
      } catch (error: any) {
        pushLog('group-initEncryption', error?.message);
      }
    },
    [checkAndSetKeyPackage, joinGroupByExternalGroups]
  );

  const refs = useRefs({ initCrypto });

  useLayoutEffect(() => {
    if (user?.userId) {
      refs.current.initCrypto(user?.userId);
    }
  }, [user, refs]);

  const value = useMemo(
    () => ({
      hasGroupKey,
      checkAndSetKeyPackage,
      createGroupEncryption,
      initCreateGroupEncryption,
      addMembersWithUpdateInfo,
      handleJoinGroup,
      handleApplyCommit,
      handleApplyCommitQueue,
      removeMembersWithUpdateInfo,
      leaveMembersWithUpdateInfo,
      joinGroupByExternal,
      initLoadGroups,
      deleteGroup,
      leaveGroup,
      encryptMessage,
      decryptMessage,
    }),
    [
      hasGroupKey,
      checkAndSetKeyPackage,
      createGroupEncryption,
      initCreateGroupEncryption,
      addMembersWithUpdateInfo,
      handleJoinGroup,
      handleApplyCommit,
      handleApplyCommitQueue,
      removeMembersWithUpdateInfo,
      leaveMembersWithUpdateInfo,
      joinGroupByExternal,
      initLoadGroups,
      deleteGroup,
      leaveGroup,
      encryptMessage,
      decryptMessage,
    ]
  );

  return value;
}
